import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Radio, ExternalLink, Loader2 } from 'lucide-react';
import { API, errText } from '../../api';

const money = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function SubscriptionCard({ plans, onLoaded }) {
  const nav = useNavigate();
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { API.mySubscriptions().then((s) => { setSubs(s); onLoaded?.(s); }).catch(() => setSubs([])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!subs || !plans) return null;
  const sub = subs.find((s) => ['active', 'past_due'].includes(s.status)) || subs[0];

  const portal = async () => {
    setBusy(true);
    try { const { url } = await API.billingPortal(); window.location.assign(url); }
    catch (e) { toast.error(errText(e, 'Could not open billing.')); setBusy(false); }
  };

  if (!sub) {
    return (
      <div className="panel rounded-3xl p-6" data-testid="subscription-card-empty">
        <p className="flex items-center gap-2 font-display text-2xl text-cream"><Radio size={18} className="text-gold" /> Social Media Management</p>
        <p className="text-sm text-cream/60 mt-2">Keep your song in rotation every week. We build the posts around your song, you approve them here, we post them on your accounts.</p>
        <p className="text-xs text-cream/45 mt-3">Plans from {money(plans.tiers.beat.monthly)}/mo &middot; quarterly, semi-annual or annual billing.</p>
        <button onClick={() => nav('/pricing#management')} data-testid="subscription-see-plans-btn" className="mt-4 btn-ghost rounded-full px-5 py-2.5 text-sm">See plans</button>
      </div>
    );
  }
  const tier = plans.tiers[sub.tier] || {};
  const term = plans.terms[sub.term] || {};
  const tone = sub.status === 'active' ? 'text-emerald-400 border-emerald-500/40' : sub.status === 'past_due' ? 'text-heart border-heart/40' : 'text-cream/50 border-cream/20';
  return (
    <div className="panel rounded-3xl p-6" data-testid="subscription-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="flex items-center gap-2 font-display text-2xl text-cream"><Radio size={18} className="text-gold" /> {tier.name}</p>
        <span className={`text-[10px] tracking-widest uppercase border rounded-full px-2 py-0.5 ${tone}`} data-testid="subscription-status">{sub.cancel_at_period_end ? 'Ends at term' : sub.status.replace('_', ' ')}</span>
      </div>
      <p className="text-sm text-cream/60 mt-1">{tier.tagline}</p>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div><dt className="eyebrow text-[9px]">Billing</dt><dd className="text-cream mt-1">{money(sub.per_term)} {term.name?.toLowerCase()}</dd></div>
        <div><dt className="eyebrow text-[9px]">{sub.status === 'canceled' ? 'Ended' : 'Renews'}</dt><dd className="text-cream mt-1" data-testid="subscription-renews">{fmtDate(sub.current_period_end)}</dd></div>
      </dl>
      <div className="mt-5 flex gap-2 flex-wrap">
        <button onClick={portal} disabled={busy} data-testid="subscription-manage-btn" className="btn-ghost rounded-full px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />} Manage billing</button>
        {sub.status === 'canceled' && <button onClick={() => nav('/pricing#management')} className="btn-gold rounded-full px-5 py-2.5 text-sm">Restart a plan</button>}
      </div>
    </div>
  );
}
